// Nuôi đồ P0: cường hóa +0…+10, phân giải, bộ lọc tự phân giải (ITEMS §8.1, §8.4).
import { BALANCE } from '@data/balance';
import { RARITIES, type ItemInstance, type Rarity } from '@data/items/types';
import type { Rng } from '../core/rng';
import { isPerfect } from './stats';
import { rarityIndex } from './generate';

// ── Phân giải
export interface SalvageYield {
  dust: number;
  refiningStone: number;
}

/** Bụi = floor((1 + Giai/10) × RARITY_DUST[phẩm]); Địa+ kèm Luyện Khí Thạch. */
export function salvageYield(item: ItemInstance): SalvageYield {
  const dust = Math.floor((1 + item.giai / 10) * BALANCE.RARITY_DUST[item.rarity]);
  const rs = (BALANCE.SALVAGE_REFINING_STONE as Partial<Record<Rarity, number>>)[item.rarity] ?? 0;
  return { dust, refiningStone: rs };
}

/**
 * Món KHÔNG BAO GIỜ tự phân giải (ITEMS §8.4): có Đạo Văn (P0 chưa có Đạo Văn Lục nên mọi Đạo Văn
 * đều "chưa có trong Lục"), set, độc bản, món đã khóa / ghim, món có dòng Cực; thêm món đã cường hóa
 * (> +0: Luyện Khí Thạch / Linh Thạch đã bỏ vào, DECISIONS M2).
 */
export function isProtected(item: ItemInstance): boolean {
  return protectReason(item) !== null;
}

export type ProtectReason = 'unique' | 'glyph' | 'perfect' | 'enhanced' | 'locked';

/** Lý do bảo vệ đầu tiên (thứ tự hiển thị ở dải hỏi phân giải tay); null = không bảo vệ. */
export function protectReason(item: ItemInstance): ProtectReason | null {
  if (item.uniqueId) return 'unique';
  if (item.daoGlyph) return 'glyph';
  if (item.lines.some(isPerfect)) return 'perfect';
  if (item.enhance > 0) return 'enhanced';
  if (item.locked) return 'locked';
  return null;
}

export interface SalvageFilter {
  enabled: boolean;
  /** Phân giải món có phẩm ≤ mức này. */
  maxRarity: Rarity;
  /** Chỉ phân giải khi không có dòng nào ≥ T2. */
  requireNoHighTier: boolean;
  /** Chỉ phân giải khi Giai món thấp hơn Giai người chơi ít nhất N; null = bỏ điều kiện. */
  giaiGap: number | null;
}

export function defaultFilter(): SalvageFilter {
  return { enabled: false, maxRarity: 'mortal', requireNoHighTier: false, giaiGap: null };
}

export function matchesFilter(item: ItemInstance, f: SalvageFilter, playerGiai: number): boolean {
  if (!f.enabled || isProtected(item)) return false;
  if (rarityIndex(item.rarity) > rarityIndex(f.maxRarity)) return false;
  if (f.requireNoHighTier && item.lines.some((l) => l.tier <= 2)) return false;
  if (f.giaiGap !== null && item.giai > playerGiai - f.giaiGap) return false;
  return true;
}

export function isRarity(v: unknown): v is Rarity {
  return typeof v === 'string' && (RARITIES as readonly string[]).includes(v);
}

// ── Cường hóa
export interface EnhanceCost {
  refiningStone: number;
  spiritStone: number;
}

/** Giá lên cấp `lv + 1`: Luyện Khí Thạch theo bảng + linh thạch 20 × cấp đích × 4^r (DECISIONS). */
export function enhanceCost(item: ItemInstance): EnhanceCost | null {
  if (item.enhance >= BALANCE.ENHANCE_MAX) return null;
  const r = Math.floor(item.giai / 10) - 1;
  return {
    refiningStone: BALANCE.ENHANCE_STONES[item.enhance]!,
    spiritStone: BALANCE.ENHANCE_SPIRIT_STONE_PER_LV * (item.enhance + 1) * Math.pow(4, r),
  };
}

/** Tỉ lệ thành công: bảng + Tích Luyện 10% mỗi lần thất bại trước đó. */
export function enhanceChance(item: ItemInstance): number {
  const base = BALANCE.ENHANCE_SUCCESS[item.enhance] ?? 0;
  return Math.min(1, base + BALANCE.ENHANCE_FAIL_BONUS * item.enhanceFailStacks);
}

// ── Chuyển cấp sang món mới cùng ô (ITEMS §8.1): giữ 80% cấp (làm tròn xuống), tốn Linh Thạch.

export interface EnhanceTransfer {
  /** Cấp món mới nhận được. */
  level: number;
  spiritStone: number;
}

/**
 * Báo giá chuyển cấp từ `from` sang `to`: chỉ khi cùng loại ô và cấp giữ lại cao hơn cấp hiện có của
 * `to`. Linh Thạch = ENHANCE_SPIRIT_STONE_PER_LV × cấp nhận × 4^r (r theo Giai món mới), tức bằng phần
 * Linh Thạch của một lần cường hóa lên đúng cấp đó; không tốn Luyện Khí Thạch, không có tỉ lệ hỏng.
 */
export function transferQuote(
  from: ItemInstance,
  to: ItemInstance,
  sameSlot: boolean,
): EnhanceTransfer | null {
  if (!sameSlot || from.uid === to.uid) return null;
  const level = Math.floor(from.enhance * BALANCE.ENHANCE_TRANSFER_KEEP);
  if (level <= to.enhance) return null;
  const r = Math.floor(to.giai / 10) - 1;
  return { level, spiritStone: BALANCE.ENHANCE_SPIRIT_STONE_PER_LV * level * Math.pow(4, r) };
}

/** Áp chuyển cấp: món mới lên `level` (Tích Luyện về 0), món cũ về +0. */
export function applyTransfer(
  from: ItemInstance,
  to: ItemInstance,
  q: EnhanceTransfer,
): { from: ItemInstance; to: ItemInstance } {
  return {
    from: { ...from, enhance: 0, enhanceFailStacks: 0 },
    to: { ...to, enhance: q.level, enhanceFailStacks: 0 },
  };
}

/** Thất bại không hạ cấp, không vỡ đồ: chỉ mất nguyên liệu và cộng Tích Luyện. */
export function enhanceOnce(item: ItemInstance, rng: Rng): { item: ItemInstance; success: boolean } {
  if (item.enhance >= BALANCE.ENHANCE_MAX) return { item, success: false };
  const success = rng.next() < enhanceChance(item);
  return success
    ? { item: { ...item, enhance: item.enhance + 1, enhanceFailStacks: 0 }, success }
    : { item: { ...item, enhanceFailStacks: item.enhanceFailStacks + 1 }, success };
}
